(() => {
  'use strict';
  if (window.hiLogoMarqueeLoaded) return;
  window.hiLogoMarqueeLoaded = true;
  const instances = new WeakMap();
  function init(root) {
    if (instances.has(root)) return;
    const cleanups = [];
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    root.querySelectorAll('.logo-marquee__viewport').forEach(viewport => {
      const rail = viewport.querySelector('.logo-marquee__rail');
      const originals = [...rail.children];
      if (!originals.length) return;
      let animation;
      let generation = 0;
      let timer;
      let lastWidth = -1;
      let disposed = false;
      function stop() {
        if (animation) animation.cancel();
        animation = null;
        rail.querySelectorAll('[data-logo-clone]').forEach(node => node.remove());
        rail.style.transform = '';
        delete viewport.dataset.marqueeReady;
      }
      async function start() {
        const version = ++generation;
        stop();
        if (!viewport.clientWidth || motion.matches || disposed) return;
        // Lazy images beyond the horizontal viewport otherwise never load and
        // keep the old all-images-loaded promise pending indefinitely.
        await Promise.all(originals.flatMap(item => [...item.querySelectorAll('img')]).map(img => {
          img.loading = 'eager';
          if (img.complete) return Promise.resolve();
          return new Promise(resolve => {
            const finish = () => { clearTimeout(timeout); img.removeEventListener('load', finish); img.removeEventListener('error', finish); resolve(); };
            const timeout = setTimeout(finish, 5000);
            img.addEventListener('load', finish, { once: true });
            img.addEventListener('error', finish, { once: true });
          });
        }));
        if (disposed || version !== generation || !viewport.clientWidth || motion.matches) return;
        const gap = parseFloat(getComputedStyle(rail).columnGap) || 0;
        const width = originals.reduce((sum, item) => sum + item.getBoundingClientRect().width, 0) + gap * originals.length;
        if (!(width > 0)) return;
        const copies = Math.max(2, Math.ceil(viewport.clientWidth / width) + 2);
        for (let copy = 1; copy < copies; copy++) {
          originals.forEach(item => {
            const clone = item.cloneNode(true);
            clone.dataset.logoClone = 'true';
            clone.setAttribute('aria-hidden', 'true');
            clone.removeAttribute('data-shopify-editor-block');
            clone.querySelectorAll('a,button,[tabindex]').forEach(node => node.setAttribute('tabindex', '-1'));
            rail.append(clone);
          });
        }
        const speed = Number(root.dataset.speed) || 80;
        const from = viewport.dataset.dir === 'right' ? -width : 0;
        const to = viewport.dataset.dir === 'right' ? 0 : -width;
        animation = rail.animate([{ transform: `translate3d(${from}px,0,0)` }, { transform: `translate3d(${to}px,0,0)` }], { duration: width / speed * 1000, iterations: Infinity, easing: 'linear' });
        viewport.dataset.marqueeReady = 'true';
        if (document.hidden) animation.pause();
      }
      const resize = new ResizeObserver(() => {
        const width = viewport.clientWidth;
        if (Math.abs(width - lastWidth) < 1) return;
        lastWidth = width;
        clearTimeout(timer);
        timer = setTimeout(start, 100);
      });
      resize.observe(viewport);
      // Re-measure if an image finishes after the initial loading timeout.
      const loaded = () => { clearTimeout(timer); timer = setTimeout(start, 100); };
      originals.forEach(item => item.querySelectorAll('img').forEach(img => img.addEventListener('load', loaded)));
      const visibility = () => { if (animation) document.hidden ? animation.pause() : animation.play(); };
      document.addEventListener('visibilitychange', visibility);
      motion.addEventListener('change', start);
      cleanups.push(() => {
        disposed = true;
        generation++;
        clearTimeout(timer);
        resize.disconnect();
        stop();
        document.removeEventListener('visibilitychange', visibility);
        motion.removeEventListener('change', start);
        originals.forEach(item => item.querySelectorAll('img').forEach(img => img.removeEventListener('load', loaded)));
      });
    });
    instances.set(root, () => cleanups.forEach(cleanup => cleanup()));
  }
  function scan(scope) { scope.querySelectorAll('.logo-marquee').forEach(init); }
  scan(document);
  document.addEventListener('shopify:section:load', event => scan(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('.logo-marquee').forEach(root => {
      const cleanup = instances.get(root);
      if (cleanup) cleanup();
      instances.delete(root);
    });
  });
})();
